import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const TOKEN_KEY = 'bfc21_token';
export const USER_KEY = 'bfc21_user';

export const mediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}${path}`;
};

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 45000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      if (!['/login', '/register', '/'].includes(path)) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const errMsg = (e, fallback = 'Une erreur est survenue') => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(' ');
  return d?.msg || e.message || fallback;
};

export const authAPI = {
  register: (data) => api.post('/auth/register', data).then((r) => r.data),
  login: (email, password) => api.post('/auth/login', { email, password }).then((r) => r.data),
  googleSession: (session_id) => api.post('/auth/google/session', { session_id }).then((r) => r.data),
  verifyPromo: (code) => api.post('/auth/verify-promo', { code }).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  logout: () => api.post('/auth/logout').then((r) => r.data),
};

export const challengesAPI = {
  list: () => api.get('/challenges').then((r) => r.data),
  badges: () => api.get('/challenges/badges').then((r) => r.data),
  day: (d) => api.get(`/challenges/${d}`).then((r) => r.data),
};

export const progressAPI = {
  dashboard: () => api.get('/progress/dashboard').then((r) => r.data),
  me: () => api.get('/progress/me').then((r) => r.data),
  validate: (data) => api.post('/progress/validate', data).then((r) => r.data),
  history: () => api.get('/progress/history').then((r) => r.data),
};

export const communityAPI = {
  list: (params) => api.get('/community/posts', { params }).then((r) => r.data),
  create: (data) => api.post('/community/posts', data).then((r) => r.data),
  like: (id) => api.post(`/community/posts/${id}/like`).then((r) => r.data),
  comments: (id) => api.get(`/community/posts/${id}/comments`).then((r) => r.data),
  addComment: (id, content) => api.post(`/community/posts/${id}/comments`, { content }).then((r) => r.data),
  report: (id) => api.post(`/community/posts/${id}/report`).then((r) => r.data),
};

export const leaderboardAPI = {
  individual: () => api.get('/leaderboard/individual').then((r) => r.data),
  entraide: () => api.get('/leaderboard/entraide').then((r) => r.data),
  full: () => api.get('/leaderboard/full').then((r) => r.data),
  clients: () => api.get('/leaderboard/clients').then((r) => r.data),
  distributeurs: () => api.get('/leaderboard/distributeurs').then((r) => r.data),
};

export const routinesAPI = {
  today: () => api.get('/routines/today').then((r) => r.data),
  set: (data) => api.put('/routines/today', data).then((r) => r.data),
  history: () => api.get('/routines/history').then((r) => r.data),
  stats: () => api.get('/routines/stats').then((r) => r.data),
  goals: () => api.get('/routines/goals').then((r) => r.data),
  setGoalTargets: (data) => api.post('/routines/goals/targets', data).then((r) => r.data),
  goalsLeaderboard: () => api.get('/routines/goals/leaderboard').then((r) => r.data),
  goalsHistory: () => api.get('/routines/goals/history').then((r) => r.data),
};

export const coachAPI = {
  overview: () => api.get('/coach/overview').then((r) => r.data),
  participants: () => api.get('/coach/participants').then((r) => r.data),
  sendMessage: (data) => api.post('/coach/message', data).then((r) => r.data),
  messages: () => api.get('/coach/messages').then((r) => r.data),
  reported: () => api.get('/coach/reported').then((r) => r.data),
  deletePost: (id) => api.delete(`/coach/posts/${id}`).then((r) => r.data),
  inbox: () => api.get('/coach/inbox').then((r) => r.data),
  exportCsv: (promo_code) => api.get('/coach/export', { params: promo_code ? { promo_code } : {}, responseType: 'blob' }).then((r) => r.data),
  proofs: () => api.get('/coach/proofs').then((r) => r.data),
  proofLog: () => api.get('/coach/proof-log').then((r) => r.data),
  adjustProof: (data) => api.post('/coach/proofs/adjust', data).then((r) => r.data),
  routines: () => api.get('/coach/routines').then((r) => r.data),
  setVisibility: (userId, hidden) => api.put(`/coach/participants/${userId}/visibility`, { hidden }).then((r) => r.data),
};

export const userAPI = {
  profile: () => api.get('/users/profile').then((r) => r.data),
  update: (data) => api.put('/users/profile', data).then((r) => r.data),
  photos: () => api.get('/users/photos').then((r) => r.data),
  updatePhotos: (data) => api.put('/users/photos', data).then((r) => r.data),
};

export const uploadAPI = {
  upload: (file, { onProgress, signal } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      signal,
      onUploadProgress: (e) => {
        if (onProgress) onProgress(Math.round((e.loaded * 100) / (e.total || 1)));
      },
    }).then((r) => r.data);
  },
};

export const videosAPI = {
  landing: () => api.get('/videos/landing').then((r) => r.data),
  day: (d) => api.get(`/videos/day/${d}`).then((r) => r.data),
};

export const coachVideosAPI = {
  list: () => api.get('/videos/manage').then((r) => r.data),
  set: (data) => api.put('/videos', data).then((r) => r.data),
  remove: (type, day_number) => api.delete('/videos', { params: { type, day_number } }).then((r) => r.data),
};

export const teamsAPI = {
  list: () => api.get('/teams').then((r) => r.data),
  mine: () => api.get('/teams/mine').then((r) => r.data),
  join: (team_id) => api.post('/teams/join', { team_id }).then((r) => r.data),
};

export const rewardsAPI = {
  list: () => api.get('/rewards').then((r) => r.data),
};

export const settingsAPI = {
  get: () => api.get('/settings').then((r) => r.data),
  update: (data) => api.put('/settings', data).then((r) => r.data),
};

export const giftsAPI = {
  list: () => api.get('/gifts').then((r) => r.data),
  manage: () => api.get('/gifts/manage').then((r) => r.data),
  create: (data) => api.post('/gifts', data).then((r) => r.data),
  update: (id, data) => api.put(`/gifts/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/gifts/${id}`).then((r) => r.data),
  reorder: (order) => api.post('/gifts/reorder', { order }).then((r) => r.data),
  eligibility: () => api.get('/gifts/eligibility').then((r) => r.data),
  award: (data) => api.post('/gifts/award', data).then((r) => r.data),
  updateAward: (id, data) => api.put(`/gifts/award/${id}`, data).then((r) => r.data),
  awards: () => api.get('/gifts/awards').then((r) => r.data),
};

export const notificationsAPI = {
  list: () => api.get('/notifications').then((r) => r.data),
  read: () => api.post('/notifications/read').then((r) => r.data),
};

export const seancesAPI = {
  unlocked: () => api.get('/videos/unlocked').then((r) => r.data),
};

export const contentAPI = {
  reglement: () => api.get('/content/reglement').then((r) => r.data),
  setReglement: (data) => api.put('/content/reglement', data).then((r) => r.data),
  secret: (n) => api.get(`/content/secret/${n}`).then((r) => r.data),
  secrets: () => api.get('/content/secrets').then((r) => r.data),
  setSecret: (data) => api.put('/content/secret', data).then((r) => r.data),
};

export default api;
